import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CASE_STUDIES, CASE_STUDY_TAGS } from "@/lib/insights";
import { useLang } from "@/lib/LangContext";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";
import EditorialMarquee from "@/components/EditorialMarquee";

export default function CaseStudiesIndex() {
  const { lang } = useLang();
  const [tag, setTag] = useState(null);

  const visible = tag ? CASE_STUDIES.filter((c) => c.tag === tag) : CASE_STUDIES;

  return (
    <div data-testid="page-case-studies">
      <Seo
        title="Case Studies · 68 NB Incident Reports | Maze Security"
        description="Sanitized incident reports from every New Brunswick community we protect. Challenge, approach, outcome — the field evidence behind the doctrine."
        path="/case-studies"
      />
      <section className="pt-32 md:pt-40 pb-16 border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ {lang === "fr" ? "Bibliothèque de cas · Rapports d'incidents" : "Case Studies · Incident Reports"} ]
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="font-serif font-light text-5xl md:text-9xl leading-[0.9] tracking-tighter"
          >
            {CASE_STUDIES.length} {lang === "fr" ? "études de cas au N.-B." : "case studies across NB."} <br />
            <i className="text-primary">{lang === "fr" ? "Un standard." : "One standard."}</i>
          </motion.h1>
          <p className="text-foreground/80 mt-8 max-w-2xl text-lg leading-relaxed">
            {lang === "fr"
              ? `Un rapport d'incident aseptisé pour chacune des ${CASE_STUDIES.length} communautés du Nouveau-Brunswick que nous servons. Défi, approche, résultat — la preuve tangible du travail derrière la doctrine.`
              : `A sanitized incident report from every one of the ${CASE_STUDIES.length} New Brunswick communities we serve. Challenge, approach, outcome — the evidence behind the doctrine.`}
          </p>
        </div>
      </section>

      {/* Tag filter */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-8 flex flex-wrap gap-2">
          <button
            onClick={() => setTag(null)}
            data-testid="case-tag-all"
            className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${
              !tag ? "bg-primary border-primary text-white" : "border-border text-muted-foreground hover:border-primary hover:text-primary"
            }`}
          >
            {lang === "fr" ? "Tous" : "All"} · {CASE_STUDIES.length}
          </button>
          {CASE_STUDY_TAGS.map((t) => {
            const count = CASE_STUDIES.filter((c) => c.tag === t).length;
            return (
              <button
                key={t}
                onClick={() => setTag(tag === t ? null : t)}
                data-testid={`case-tag-${t.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${
                  tag === t ? "bg-primary border-primary text-white" : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                }`}
              >
                {t} · {count}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {visible.map((c, i) => (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05, ease: [0.76, 0, 0.24, 1] }}
                className="border-r border-b border-border"
              >
                <Link
                  to={`/case-studies/${c.slug}`}
                  data-testid={`case-card-${c.slug}`}
                  className="block p-8 h-full group hover:bg-[hsl(0_0%_7%)] transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className="font-mono text-[10px] tracking-widest text-primary">
                      {String(i + 1).padStart(3, "0")}
                    </span>
                    <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
                      {c.date}
                    </span>
                  </div>
                  <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-2">
                    {c.region}
                  </div>
                  <h3 className="font-serif text-3xl leading-tight tracking-tight mb-3 group-hover:text-primary transition-colors">
                    {c.cityName}
                  </h3>
                  <p className="font-serif italic text-lg text-foreground/85 leading-snug border-t border-border pt-4">
                    {c.threat}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee />
      <QuoteForm />
    </div>
  );
}
